import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Field } from "redux-form";

import { Box, Stack } from "@braintrust/braintrust-ui-components";
import { reduxForm } from "@js/forms/components";
import { Form } from "@js/forms/components/form";
import { WARNING_MODAL_TYPE } from "@js/forms/hooks";
import { NavLayoutContainer } from "@js/layouts/app";
import type { JobFormValues, ManagedJob } from "@js/types/jobs";

import { JobInterviewGradingCriteria } from "../../components/job-interview-grading-criteria";
import { JobInterviewIntroVideo } from "../../components/job-interview-intro-video";
import { JobInterviewQuestions } from "../../components/job-interview-questions";
import {
  InterviewQuestionsHeader,
  SubmitButton,
} from "../../components/job-interview-questions-header";
import { JobInterviewSettings } from "../../components/job-interview-settings";
import { INTERVIEW_QUESTIONS_FORM_ID } from "../../constants";
import { gradingCriteriaValidator, questionsValidator } from "../../validator";

import { InterviewQuestionFormCover } from "./interview-question-form-cover";

import styles from "./style.module.scss";

type InterviewQuestionsFormComponentCustomProps = {
  customContent?: React.ReactNode;
  isLoading: boolean;
  job: ManagedJob | undefined;
};

type InterviewQuestionsFormComponentProps = {
  submitting: boolean;
  submit: () => void;
} & InterviewQuestionsFormComponentCustomProps;

export const InterviewQuestionsFormComponent = ({
  submit,
  submitting,
  customContent,
  isLoading,
  job,
}: InterviewQuestionsFormComponentProps) => {
  const { state } = useLocation();
  const [questionsWereChanged, setQuestionsWereChanged] = useState(false);
  const prevPath = state?.prevPath || "";
  const prevPathProcessed = prevPath.startsWith("/auth") ? undefined : prevPath;
  const backLink = prevPathProcessed ?? `/jobs/${job?.id}/interviews`;
  return (
    <Form
      onSubmit={submitting ? submit : null}
      style={{ width: "100%", display: "flex", flexDirection: "column" }}
      id={INTERVIEW_QUESTIONS_FORM_ID}
    >
      <NavLayoutContainer className={styles.interviewHeader}>
        <InterviewQuestionsHeader
          backLink={backLink}
          isLoading={isLoading}
          userCanManage={!!job?.user_can_manage}
        />
      </NavLayoutContainer>
      {customContent ? (
        customContent
      ) : (
        <>
          <Box className={styles.interviewQuestionForm}>
            <Stack sx={{ gap: { xs: 2, sm: 3 } }}>
              <JobInterviewSettings />
              <JobInterviewIntroVideo />
            </Stack>
            <Stack sx={{ gap: { xs: 2, sm: 3 } }}>
              <Field
                name="interview_questions"
                component={JobInterviewQuestions}
                validate={[questionsValidator]}
                handleQuestionsChange={() => setQuestionsWereChanged(true)}
              />
              <Field
                name="interview_criteria"
                jobId={job?.id as number}
                component={JobInterviewGradingCriteria}
                validate={[gradingCriteriaValidator]}
                questionsWereChanged={questionsWereChanged}
                onRegenerateCriteria={() => setQuestionsWereChanged(false)}
              />
              <Box sx={{ display: "flex", justifyContent: "end" }}>
                <SubmitButton />
              </Box>
            </Stack>
          </Box>

          <Field
            name="is_ai_interview_enabled"
            component={InterviewQuestionFormCover}
          />
        </>
      )}
    </Form>
  );
};

export const InterviewQuestionsForm = reduxForm<
  JobFormValues,
  InterviewQuestionsFormComponentCustomProps
>({
  form: INTERVIEW_QUESTIONS_FORM_ID,
  unsavedChangesWarning: true,
  unsavedChangesWarningModelType: WARNING_MODAL_TYPE.AI_INTERVIEW_QUESTION,
  enableReinitialize: true,
})(({ submitting, submit, customContent, job, isLoading }) => {
  return (
    <InterviewQuestionsFormComponent
      submit={submit}
      submitting={submitting}
      customContent={customContent}
      isLoading={isLoading}
      job={job}
    />
  );
});
